export type CalculatorInputBand = {
  readonly id: string;
  readonly label: string;
  readonly min?: number;
  readonly max?: number;
};

export type CalculatorInputOption = {
  readonly id: string;
  readonly label: string;
  readonly description?: string;
  readonly numericValue: number;
  readonly bandKey?: string;
};

export type CalculatorInputDefinition =
  | {
      readonly id: string;
      readonly kind: "number";
      readonly label: string;
      readonly description?: string;
      readonly min: number;
      readonly max: number;
      readonly step?: number;
      readonly defaultValue: number;
      readonly bands?: readonly CalculatorInputBand[];
      readonly trackingKey?: string;
    }
  | {
      readonly id: string;
      readonly kind: "single_select";
      readonly label: string;
      readonly description?: string;
      readonly defaultOptionId: string;
      readonly options: readonly CalculatorInputOption[];
      readonly trackingKey?: string;
    };

export type CalculatorFormula =
  | { readonly kind: "constant"; readonly value: number }
  | { readonly kind: "input"; readonly inputId: string }
  | { readonly kind: "metric"; readonly metricId: string }
  | { readonly kind: "sum"; readonly terms: readonly CalculatorFormula[] }
  | { readonly kind: "difference"; readonly initial: CalculatorFormula; readonly subtract: readonly CalculatorFormula[] }
  | { readonly kind: "product"; readonly factors: readonly CalculatorFormula[] }
  | { readonly kind: "ratio"; readonly numerator: CalculatorFormula; readonly denominator: CalculatorFormula; readonly multiplier?: number };

export type CalculatorMetricBand = {
  readonly id: string;
  readonly label: string;
  readonly summary?: string;
  readonly min?: number;
  readonly max?: number;
};

export type CalculatorMetricDefinition = {
  readonly id: string;
  readonly label: string;
  readonly formula: CalculatorFormula;
  readonly precision?: number;
  readonly prefix?: string;
  readonly suffix?: string;
  readonly bands?: readonly CalculatorMetricBand[];
  readonly trackingKey?: string;
};

export type CalculatorSchema = {
  readonly id: string;
  readonly inputs: readonly CalculatorInputDefinition[];
  readonly metrics: readonly CalculatorMetricDefinition[];
};

export type CalculatorInputValues = Readonly<Record<string, number | string | undefined>>;

export type CalculatorMetricResult = {
  readonly id: string;
  readonly label: string;
  readonly value: number;
  readonly displayValue: string;
  readonly band?: CalculatorMetricBand;
  readonly trackingKey?: string;
};

export type CalculatorEvaluation = {
  readonly schemaId: string;
  readonly inputValues: Readonly<Record<string, number>>;
  readonly inputBands: Readonly<Record<string, string>>;
  readonly metrics: readonly CalculatorMetricResult[];
  readonly trackingPayload: Readonly<Record<string, string>>;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

function bandFor(value: number, bands: readonly CalculatorInputBand[] | readonly CalculatorMetricBand[] = []) {
  return bands.find((band) => {
    const aboveMin = band.min === undefined || value >= band.min;
    const belowMax = band.max === undefined || value <= band.max;
    return aboveMin && belowMax;
  });
}

function normalizeNumberInput(input: Extract<CalculatorInputDefinition, { kind: "number" }>, value: unknown): number {
  const parsed = typeof value === "number" ? value : Number(value);
  const safeValue = Number.isFinite(parsed) ? parsed : input.defaultValue;
  const steppedValue = input.step && input.step > 0 ? Math.round(safeValue / input.step) * input.step : safeValue;

  return clamp(steppedValue, input.min, input.max);
}

function normalizeSelectInput(input: Extract<CalculatorInputDefinition, { kind: "single_select" }>, value: unknown): number {
  const selectedId = typeof value === "string" && value ? value : input.defaultOptionId;
  const selectedOption =
    input.options.find((option) => option.id === selectedId) ??
    input.options.find((option) => option.id === input.defaultOptionId) ??
    input.options[0];

  return selectedOption?.numericValue ?? 0;
}

function selectedBand(input: CalculatorInputDefinition, value: unknown, numericValue: number): string {
  if (input.kind === "single_select") {
    const selectedId = typeof value === "string" && value ? value : input.defaultOptionId;
    const selectedOption =
      input.options.find((option) => option.id === selectedId) ??
      input.options.find((option) => option.id === input.defaultOptionId) ??
      input.options[0];

    return selectedOption?.bandKey || selectedOption?.id || "unknown";
  }

  return bandFor(numericValue, input.bands)?.id ?? "unbucketed";
}

function evaluateFormula(
  formula: CalculatorFormula,
  inputValues: Readonly<Record<string, number>>,
  metricValues: Readonly<Record<string, number>>,
): number {
  switch (formula.kind) {
    case "constant":
      return formula.value;
    case "input":
      return inputValues[formula.inputId] ?? 0;
    case "metric":
      return metricValues[formula.metricId] ?? 0;
    case "sum":
      return formula.terms.reduce((sum, term) => sum + evaluateFormula(term, inputValues, metricValues), 0);
    case "difference":
      return formula.subtract.reduce(
        (value, term) => value - evaluateFormula(term, inputValues, metricValues),
        evaluateFormula(formula.initial, inputValues, metricValues),
      );
    case "product":
      return formula.factors.reduce((value, factor) => value * evaluateFormula(factor, inputValues, metricValues), 1);
    case "ratio": {
      const denominator = evaluateFormula(formula.denominator, inputValues, metricValues);
      if (denominator === 0) {
        return 0;
      }

      return (evaluateFormula(formula.numerator, inputValues, metricValues) / denominator) * (formula.multiplier ?? 1);
    }
  }
}

function formatMetric(value: number, metric: CalculatorMetricDefinition): string {
  const precision = metric.precision ?? 0;
  const formatted = value.toLocaleString("en-US", {
    maximumFractionDigits: precision,
    minimumFractionDigits: precision,
  });

  return `${metric.prefix ?? ""}${formatted}${metric.suffix ?? ""}`;
}

export function evaluateCalculator(schema: CalculatorSchema, values: CalculatorInputValues): CalculatorEvaluation {
  const inputValues: Record<string, number> = {};
  const inputBands: Record<string, string> = {};
  const trackingPayload: Record<string, string> = {};

  schema.inputs.forEach((input) => {
    const rawValue = values[input.id];
    const numericValue = input.kind === "number" ? normalizeNumberInput(input, rawValue) : normalizeSelectInput(input, rawValue);
    const inputBand = selectedBand(input, rawValue, numericValue);

    inputValues[input.id] = numericValue;
    inputBands[input.id] = inputBand;

    if (input.trackingKey) {
      trackingPayload[input.trackingKey] = inputBand;
    }
  });

  const metricValues: Record<string, number> = {};
  const metrics = schema.metrics.map((metric) => {
    const value = evaluateFormula(metric.formula, inputValues, metricValues);
    const band = bandFor(value, metric.bands);
    metricValues[metric.id] = value;

    if (metric.trackingKey && band) {
      trackingPayload[metric.trackingKey] = band.id;
    }

    return {
      id: metric.id,
      label: metric.label,
      value,
      displayValue: formatMetric(value, metric),
      band,
      trackingKey: metric.trackingKey,
    };
  });

  return {
    schemaId: schema.id,
    inputValues,
    inputBands,
    metrics,
    trackingPayload,
  };
}
